import { Alert } from '@ksyos/design-system';
import { useEffect } from 'react';
import type { ITfoDiagnosticReportCustomControlData } from '@common/validation/customControls/ophthalmology';
import type { ValidationErrors } from '@frontend/utils/validation';
import { tfoConvertOldToNewScreeningStandard } from '@common/customControls/tfoV1ConvertOldToNewScreeningStandard';
import {
    Maculopathie,
    Policies,
    Retinopathie,
    retinopathieOptions,
    retinopathieOptionsForMaculopathieM1,
} from '@common/validation/customControls/ophthalmology';
import { ListHeader } from '@frontend/components/elements/common';
import { FormContextProvider } from '@frontend/components/elements/formContext';
import { FormLabel } from '@frontend/components/elements/forms/FormLabel';
import { HorizontalFormGroup } from '@frontend/components/elements/forms/HorizontalFormGroup';
import { InputContainer } from '@frontend/components/elements/forms/InputContainer';
import { RadioGroupField } from '@frontend/components/fields/RadioGroup';
import { TextAreaField } from '@frontend/components/fields/TextArea';
import { TriStateField } from '@frontend/components/fields/TriState';
import { NewStandardInfo } from './NewStandardInfo';
import { OldFundusscreeningPolicyAndUrgencyAddEditFields } from './OldFundusscreeningPolicyAndUrgencyAddEditFields';

interface TfoV1DiagnosticReportAddEditCustomControlFieldsProps {
    customControlData: ITfoDiagnosticReportCustomControlData;
    errors: ValidationErrors;
    handleChangeCustomControl: (values: Partial<ITfoDiagnosticReportCustomControlData>) => Promise<void>;
    isBiograder?: boolean;
    showAdvisedPolicy: boolean;
    showToelichting?: boolean;
    validateOneCustomControl: (name: string) => Promise<void>;
}

const maculopathieOptions = [Maculopathie.m0, Maculopathie.m1, Maculopathie.nietTeBeoordelen];
const advisedPolicies = [
    Policies.controleVolgensRichtlijn,
    Policies.teleConsultOogarts,
    Policies.verwijzingOogartsDoorHuisarts,
    Policies.verwijzingOogartsDoorOptometrist,
    Policies.vervolgonderzoekOptometrist,
    Policies.anders,
];

export function OldTfoV1DiagnosticReportAddEditCustomControlFields(
    props: Readonly<TfoV1DiagnosticReportAddEditCustomControlFieldsProps>
) {
    useEffect(() => {
        if (hasToConvertOldToNewScreeningStandard(props.customControlData)) {
            props.handleChangeCustomControl({
                retinopathie_od: tfoConvertOldToNewScreeningStandard(props.customControlData.bevindingen_npdr_od),
                retinopathie_os: tfoConvertOldToNewScreeningStandard(props.customControlData.bevindingen_npdr_os),
            });
        }
    }, [props.customControlData]);

    return (
        <FormContextProvider
            value={{
                errors: props.errors,
                item: props.customControlData,
                onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                onDoValidate: async (key) => await props.validateOneCustomControl(key),
                placeholderGroupName: 'diagnosticReport',
            }}
        >
            <NewStandardInfo />

            {[props.customControlData.maculopathie_od, props.customControlData.maculopathie_os].includes(
                Maculopathie.m1
            ) && <Alert appearance="warning">M1 gaat altijd gepaard met R1, R2 of R3</Alert>}
            <HorizontalFormGroup>
                <FormLabel isRequired>Retinopathie</FormLabel>
                <InputContainer>
                    <ListHeader>OD</ListHeader>
                    <RadioGroupField
                        automation="retinopathyOd"
                        error={props.errors.get('customControlData.retinopathie_od')}
                        fieldKey="retinopathie_od"
                        hasErrors={props.errors.has('customControlData.retinopathie_od')}
                        label=""
                        name="retinopathie_od"
                        onChange={async (value) => {
                            await props.handleChangeCustomControl({
                                geadviseerd_beleid: null,
                                retinopathie_od: value as Retinopathie,
                            });
                        }}
                        options={
                            props.customControlData.maculopathie_od === Maculopathie.m1
                                ? retinopathieOptionsForMaculopathieM1
                                : retinopathieOptions
                        }
                        radioButtonsStacked={true}
                        value={
                            props.customControlData.retinopathie_od ??
                            tfoConvertOldToNewScreeningStandard(props.customControlData.bevindingen_npdr_od)
                        }
                    />
                </InputContainer>
                <InputContainer>
                    <ListHeader>OS</ListHeader>
                    <RadioGroupField
                        automation="retinopathyOs"
                        error={props.errors.get('customControlData.retinopathie_os')}
                        fieldKey="retinopathie_os"
                        hasErrors={props.errors.has('customControlData.retinopathie_os')}
                        label=""
                        name="retinopathie_os"
                        onChange={async (value) => {
                            await props.handleChangeCustomControl({
                                geadviseerd_beleid: null,
                                retinopathie_os: value as Retinopathie,
                            });
                        }}
                        options={
                            props.customControlData.maculopathie_os === Maculopathie.m1
                                ? retinopathieOptionsForMaculopathieM1
                                : retinopathieOptions
                        }
                        radioButtonsStacked={true}
                        value={
                            props.customControlData.retinopathie_os ??
                            tfoConvertOldToNewScreeningStandard(props.customControlData.bevindingen_npdr_os)
                        }
                    />
                </InputContainer>
            </HorizontalFormGroup>
            <HorizontalFormGroup>
                <FormLabel isRequired>Maculopathie</FormLabel>
                <InputContainer>
                    <RadioGroupField
                        automation="maculopathyOd"
                        error={props.errors.get('customControlData.maculopathie_od')}
                        fieldKey="maculopathie_od"
                        hasErrors={props.errors.has('customControlData.maculopathie_od')}
                        label=""
                        name="maculopathie_od"
                        onChange={async (value) => {
                            await props.handleChangeCustomControl({
                                geadviseerd_beleid: null,
                                maculopathie_od: value as Maculopathie,
                                ...(value === Maculopathie.m1 &&
                                props.customControlData.retinopathie_od === Retinopathie.r0
                                    ? { retinopathie_od: null }
                                    : {}),
                            });
                        }}
                        options={maculopathieOptions}
                        radioButtonsStacked={true}
                        value={props.customControlData.maculopathie_od}
                    />
                </InputContainer>
                <InputContainer>
                    <RadioGroupField
                        automation="maculopathyOs"
                        error={props.errors.get('customControlData.maculopathie_os')}
                        fieldKey="maculopathie_os"
                        hasErrors={props.errors.has('customControlData.maculopathie_os')}
                        label=""
                        name="maculopathie_os"
                        onChange={async (value) => {
                            await props.handleChangeCustomControl({
                                geadviseerd_beleid: null,
                                maculopathie_os: value as Maculopathie,
                                ...(value === Maculopathie.m1 &&
                                props.customControlData.retinopathie_os === Retinopathie.r0
                                    ? { retinopathie_os: null }
                                    : {}),
                            });
                        }}
                        options={maculopathieOptions}
                        radioButtonsStacked
                        value={props.customControlData.maculopathie_os}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            <HorizontalFormGroup
                hasErrors={
                    props.errors.has('customControlData.andere_bevindingen_od') ||
                    props.errors.has('customControlData.andere_bevindingen_os')
                }
            >
                <FormLabel>Andere bevindingen</FormLabel>
                <InputContainer>
                    <TriStateField
                        automation="otherFindingsOd"
                        error={props.errors.get('customControlData.andere_bevindingen_od')}
                        fieldKey="andere_bevindingen_od"
                        hasErrors={props.errors.has('customControlData.andere_bevindingen_od')}
                        label=""
                        onChange={async (value) => {
                            await props.handleChangeCustomControl({
                                andere_bevindingen_od: value,
                                ...(!value ? { andere_bevindingen_od_text: null } : {}),
                            });
                        }}
                        value={props.customControlData.andere_bevindingen_od}
                    />
                </InputContainer>
                <InputContainer>
                    <TriStateField
                        automation="otherFindingsOs"
                        error={props.errors.get('customControlData.andere_bevindingen_os')}
                        fieldKey="andere_bevindingen_os"
                        label=""
                        onChange={async (value) => {
                            await props.handleChangeCustomControl({
                                andere_bevindingen_os: value,
                                ...(!value ? { andere_bevindingen_os_text: null } : {}),
                            });
                        }}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            <HorizontalFormGroup
                hasErrors={
                    props.errors.has('customControlData.andere_bevindingen_od_text') ||
                    props.errors.has('customControlData.andere_bevindingen_os_text')
                }
            >
                <FormLabel> </FormLabel>
                {props.customControlData.andere_bevindingen_od && (
                    <>
                        <InputContainer>
                            <TextAreaField
                                automation="otherFindingsOdText"
                                disabled={!props.customControlData.andere_bevindingen_od}
                                error={props.errors.get('customControlData.andere_bevindingen_od_text')}
                                fieldKey="andere_bevindingen_od_text"
                                isRequired
                                label=""
                                placeholder="Beschrijf bevindingen..."
                            />
                        </InputContainer>
                        {!props.customControlData.andere_bevindingen_os && <InputContainer></InputContainer>}
                    </>
                )}
                {props.customControlData.andere_bevindingen_os && (
                    <>
                        {!props.customControlData.andere_bevindingen_od && <InputContainer></InputContainer>}
                        <InputContainer>
                            <TextAreaField
                                automation="otherFindingsOsText"
                                disabled={!props.customControlData.andere_bevindingen_os}
                                error={props.errors.get('customControlData.andere_bevindingen_os_text')}
                                fieldKey="andere_bevindingen_os_text"
                                isRequired
                                label=""
                                placeholder="Beschrijf bevindingen..."
                            />
                        </InputContainer>
                    </>
                )}
            </HorizontalFormGroup>

            {props.showAdvisedPolicy && (
                <OldFundusscreeningPolicyAndUrgencyAddEditFields
                    advisedPolicies={advisedPolicies}
                    customControlData={props.customControlData}
                    errors={props.errors}
                    handleChangeCustomControl={props.handleChangeCustomControl}
                    requiredPolicy={true}
                />
            )}

            {props.showToelichting && (
                <TextAreaField
                    error={props.errors.get('customControlData.toelichting')}
                    fieldKey="toelichting"
                    hasErrors={props.errors.has('customControlData.toelichting')}
                    label="Toelichting"
                    placeholder="Een toelichting, opmerkingen of vragen..."
                    value={props.customControlData.toelichting}
                />
            )}
        </FormContextProvider>
    );
}

function hasToConvertOldToNewScreeningStandard(customControlData: ITfoDiagnosticReportCustomControlData) {
    const hasOldSreeningStandardValues =
        !!customControlData.bevindingen_npdr_od || !!customControlData.bevindingen_npdr_os;

    const isMissingNewScreeningStandardValues =
        !customControlData.retinopathie_od || !customControlData.retinopathie_os;

    return hasOldSreeningStandardValues && isMissingNewScreeningStandardValues;
}

export function tfoV1DiagnosticReportInitializeCustomControlData() {
    return {
        andere_bevindingen_od: null,
        andere_bevindingen_od_text: null,
        andere_bevindingen_os: null,
        andere_bevindingen_os_text: null,
        geadviseerd_beleid: null,
        geadviseerd_beleid_anders: null,
        maculopathie_od: null,
        maculopathie_os: null,
        retinopathie_od: null,
        retinopathie_os: null,
        toelichting: null,
        urgentie: null,
    };
}
